import { Form, message, Modal } from "antd";
import { FC, useEffect, useMemo, useState } from "react";
import { useRequest } from "ahooks";
import classNames from "classnames";

import styles from "./index.less";

import InfoIcon from "../../assets/svg/info.svg";

import PayInputNumber from "./components/PayInputNumber";
import PaySelectMethod from "./components/PaySelectMethod";
import PaySelectPeriod from "./components/PaySelectPeriod";
import PayInformation from "./components/PayInformation";

import {
  IPriceInformation,
  PlanPayModalTypeEnum,
  PricingPeriodEnum,
} from "../../types";
import {
  calcAmountService,
  ConsumptionType,
  getBalanceService,
  getPaySuccessInformationService,
  IDuration,
  IPaySuccessInformation,
  IServiceOrderDto,
  OrderInfo,
  PayType,
} from "../../service";
import { ResponseCode } from "../../constants";

import PlanPayWithBankTransferSuccessModal from "../PlanPayWithBankTransferSuccessModal";
import PlanPaySuccessModal from "../PlanPaySuccessModal";
import AllocatingResourcesModal from "./components/AllocatingResourcesModal";

interface PlanPayModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  type: PlanPayModalTypeEnum;
  priceInformation?: IPriceInformation;
  allowedMethods: PayType[];
  initialValues?: {
    period: PricingPeriodEnum;
    planNum: number;
    payMethod: PayType;
  };
}

const PlanPayModal: FC<PlanPayModalProps> = ({
  open,
  onClose,
  title,
  type,
  allowedMethods,
  priceInformation,
  initialValues,
}) => {
  const [
    openPlanPayWithBankTransferSuccessModal,
    setOpenPlanPayWithBankTransferSuccessModal,
  ] = useState<boolean>(false);
  const [openPlanPaySuccessModal, setOpenPlanPaySuccessModal] =
    useState<boolean>(false);
  const [paySuccessInformation, setPaySuccessInformation] =
    useState<IPaySuccessInformation>();
  const [pollingAllocatingResources, setPollingAllocatingResources] =
    useState<boolean>(false);
  const [orderInfo, setOrderInfo] = useState<OrderInfo>();

  const [form] = Form.useForm();
  const period = Form.useWatch("period", form);
  const planNum = Form.useWatch("planNum", form);
  const payMethod = Form.useWatch("payMethod", form);

  const payInformation = useMemo(() => {
    if (!priceInformation) {
      return void 0;
    }

    const planDuration: IDuration =
      period === PricingPeriodEnum.MONTHLY
        ? {
            id: priceInformation.monthlyId,
            amount: priceInformation.monthlyPrice,
          }
        : {
            id: priceInformation.yearlyId,
            amount: priceInformation.yearlyPrice,
          };

    return {
      planNum,
      planDuration,
      consumptionType:
        type === PlanPayModalTypeEnum.Subscribe
          ? ConsumptionType.MEMBER
          : type === PlanPayModalTypeEnum.Renew
          ? ConsumptionType.RENEW_MEMBER
          : ConsumptionType.UPGRADE_MEMBER,
    } as IServiceOrderDto;
  }, [priceInformation, planNum, period, payMethod, type]);

  const { data: previewPrice } = useRequest(
    async () => {
      if (!payInformation) {
        return void 0;
      }

      const rsp = await calcAmountService(payInformation);

      if (rsp.code !== ResponseCode.Success) {
        message.error(rsp.msg);
        return void 0;
      }

      return rsp.data.amount ?? void 0;
    },
    {
      ready: open,
      refreshDeps: [payInformation],
      debounceWait: 200,
    }
  );

  const { data: balance } = useRequest(
    async () => {
      const rsp = await getBalanceService();

      if (rsp.code !== ResponseCode.Success) {
        message.error(rsp.msg);
        return void 0;
      }

      return rsp.data?.canUseBalance;
    },
    {
      ready: allowedMethods.includes(PayType.BALANCE) && open,
      refreshDeps: [open, allowedMethods],
    }
  );

  const {
    run: getPaySuccessInformation,
    cancel: cancelGetPaySuccessInformation,
  } = useRequest(
    async (payId: string) => {
      const rsp = await getPaySuccessInformationService(payId);

      if (rsp.code !== ResponseCode.Success) {
        message.error(rsp.msg);
        cancelGetPaySuccessInformation();
        return;
      }

      if (!!rsp.data) {
        cancelGetPaySuccessInformation();
        setPollingAllocatingResources(false);
        setPaySuccessInformation(rsp.data);
        setOpenPlanPaySuccessModal(true);
      }
    },
    {
      pollingInterval: 1000,
      pollingWhenHidden: false,
      manual: true,
    }
  );

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues]);

  useEffect(() => {
    setOrderInfo(void 0);
  }, [period, planNum]);

  useEffect(() => {
    form.setFieldsValue({
      planNum: 1,
    });
  }, [period]);

  return (
    <>
      <Modal
        centered
        destroyOnClose
        maskClosable={false}
        title={title}
        width={640}
        open={open}
        footer={null}
        onCancel={() => {
          form.resetFields();
          setOrderInfo(void 0);
          onClose();
        }}
        onClose={() => {
          form.resetFields();
          setOrderInfo(void 0);
          onClose();
        }}
        classNames={{
          header: styles.header,
        }}
      >
        <div className={styles.container}>
          <Form className={styles.form} form={form} layout="vertical">
            <Form.Item
              name="period"
              className={classNames({
                [styles.hidden]: type === PlanPayModalTypeEnum.Upgrade,
              })}
            >
              <PaySelectPeriod priceInformation={priceInformation} />
            </Form.Item>

            <Form.Item
              name="planNum"
              className={classNames({
                [styles.hidden]: type === PlanPayModalTypeEnum.Upgrade,
              })}
              label={
                <div className={styles.label}>
                  {[
                    PlanPayModalTypeEnum.Subscribe,
                    PlanPayModalTypeEnum.Upgrade,
                  ].includes(type)
                    ? "订阅时长"
                    : "续费时长"}
                  （{period === PricingPeriodEnum.MONTHLY ? "月" : "年"}）
                </div>
              }
            >
              <PayInputNumber />
            </Form.Item>

            {type === PlanPayModalTypeEnum.Upgrade && (
              <div className={styles["upgrade-tip"]}>
                <div className={styles["title"]}>
                  <img src={InfoIcon} alt="info" className={styles["icon"]} />
                  <div className={styles["text"]}>升级提示</div>
                </div>
                <div className={styles["desc"]}>
                  <div>
                    1、您当前订阅的剩余时间（含未生效天数）将全部转化为高版本订阅时长
                  </div>
                  <div>
                    2、版本升级后，动能值将按「版本剩余天数/当月总天数」*「新版本所含额度」的比例进行下发
                  </div>
                </div>
              </div>
            )}

            <Form.Item
              name="payMethod"
              label={
                <div
                  className={styles.label}
                  style={{
                    marginTop: type === PlanPayModalTypeEnum.Upgrade ? 0 : 24,
                  }}
                >
                  支付方式
                </div>
              }
            >
              <PaySelectMethod
                payAmount={previewPrice}
                balance={balance}
                allowedMethods={
                  period === PricingPeriodEnum.YEARLY
                    ? allowedMethods
                    : allowedMethods.filter((m) => m !== PayType.OFFLINE)
                }
              />
            </Form.Item>
          </Form>

          <PayInformation
            balance={balance}
            payMethod={payMethod}
            payAmount={previewPrice}
            payInformation={payInformation}
            onSuccess={(payId: string) => {
              onClose();
              form.resetFields();

              // 开启轮询
              setPollingAllocatingResources(true);
              getPaySuccessInformation(payId);
            }}
            onPayWithBankTransferConfirm={() => {
              onClose();
              form.resetFields();
              setOpenPlanPayWithBankTransferSuccessModal(true);
            }}
            orderInfo={orderInfo}
            setOrderInfo={setOrderInfo}
          />
        </div>
      </Modal>

      <AllocatingResourcesModal open={pollingAllocatingResources} />

      <PlanPaySuccessModal
        type={type}
        planId={payInformation?.planDuration.id ?? ""}
        paySuccessInformation={paySuccessInformation}
        open={openPlanPaySuccessModal}
        onClose={() => {
          setPaySuccessInformation(void 0);
          setOpenPlanPaySuccessModal(false);
        }}
      />

      <PlanPayWithBankTransferSuccessModal
        open={openPlanPayWithBankTransferSuccessModal}
        onClose={() => {
          setOpenPlanPayWithBankTransferSuccessModal(false);
        }}
        onOk={() => {
          setOpenPlanPayWithBankTransferSuccessModal(false);
        }}
      />
    </>
  );
};

export default PlanPayModal;
